import React from 'react';
import { Container, Form, FormControl, Button, ListGroup } from 'react-bootstrap';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import ProductCardElement from '../components/productSlider/utility';
import useSeo from '../services/useSeo';

const SearchPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [keyword, setKeyword] = useState(searchParams.get('q') || '');
    const [searchResult, setSearchResult] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [popular, setPopular] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [currentSuggestion, setCurrentSuggestion] = useState(0);
    const boxRef = useRef(null);
    const suggestTimer = useRef(null);

    // Search result pages are thin, near-duplicate content and ?q= can generate
    // unlimited crawlable URLs. Canonical every variant to the bare /search page
    // and noindex the result views so they don't become duplicates.
    const activeQuery = searchParams.get('q');
    useSeo({
        title: activeQuery
            ? `搜尋「${activeQuery}」 | 琴語鋼琴網`
            : '歌曲搜尋 | 琴語鋼琴網',
        description: '搜尋琴語鋼琴網的鋼琴譜與簡譜，依歌名、歌手或作曲家查找。',
        canonicalPath: '/search',
        noindex: Boolean(activeQuery),
    });

    const searchSuggestions = [
        "周杰倫 稻香",
        "五月天 星空 簡譜",
        "周杰倫 告白氣球",
        "周興哲 怎麼了 ",
        "告五人 又到天黑"
    ];

    // PLACEHOLDER_EFFECTS
    // The actual search request. Wrapped so both the button and a URL ?q= can call it.
    const runSearch = useCallback(async (term) => {
        const q = (term ?? '').trim();
        if (q === '') return;
        setLoading(true);
        setShowSuggestions(false);
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + `/api/search?keyword=${encodeURIComponent(q)}`, { method: 'GET' });
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            setSearchResult(Array.isArray(data) ? data : []);
        } catch (error) {
            // Don't yank the user off to the home page on a transient error —
            // show an empty result with recovery options instead.
            console.error('There was a problem with the search request:', error);
            setSearchResult([]);
        } finally {
            setSearched(true);
            setLoading(false);
        }
    }, []);

    // Run a search whenever the URL ?q= changes (initial load, back/forward,
    // shared link). This makes results shareable and refresh-safe.
    useEffect(() => {
        const q = searchParams.get('q');
        if (q && q.trim() !== '') {
            setKeyword(q);
            runSearch(q);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, runSearch]);

    // Load popular songs once, to show as a fallback when there are no results.
    useEffect(() => {
        fetch(process.env.REACT_APP_API_URL + '/api/gettopmonth')
            .then(res => res.ok ? res.json() : [])
            .then(data => setPopular(Array.isArray(data) ? data.slice(0, 8) : []))
            .catch(() => {});
    }, []);

    // Rotate the placeholder hint only while the box is empty and idle.
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSuggestion((prev) => (prev + 1) % searchSuggestions.length);
        }, 2000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [searchResult]);

    // Close the suggestion dropdown when clicking outside the search box.
    useEffect(() => {
        const onClick = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setShowSuggestions(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);


    // PLACEHOLDER_HANDLERS
    // Debounced type-ahead: fetch suggestions ~250ms after the user stops typing.
    const handlechange = (e) => {
        const val = e.target.value;
        setKeyword(val);
        if (suggestTimer.current) clearTimeout(suggestTimer.current);
        const q = val.trim();
        if (q === '') {
            setSuggestions([]);
            setShowSuggestions(false);
            setActiveIndex(-1);
            return;
        }
        suggestTimer.current = setTimeout(async () => {
            try {
                const res = await fetch(process.env.REACT_APP_API_URL + `/api/suggest?keyword=${encodeURIComponent(q)}`, { method: 'GET' });
                const data = await res.json();
                setSuggestions(Array.isArray(data) ? data : []);
                setShowSuggestions(true);
                setActiveIndex(-1);
            } catch {
                setSuggestions([]);
            }
        }, 250);
    };

    // Keyboard navigation for the autocomplete dropdown (Arrow keys / Enter / Escape).
    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        } else if (e.key === 'Enter') {
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
                e.preventDefault();
                pickSuggestion(suggestions[activeIndex].song_name);
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setActiveIndex(-1);
        }
    };

    // Push the term into the URL; the ?q= effect performs the actual search.
    const submitSearch = (term) => {
        const q = (term ?? keyword).trim();
        if (q === '') return;
        setSearchParams({ q });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        submitSearch();
    };

    const pickSuggestion = (name) => {
        setKeyword(name);
        setShowSuggestions(false);
        submitSearch(name);
    };


    // PLACEHOLDER_RENDER
    const hasResults = searchResult.length > 0;

    const renderGrid = (items) => (
        <div className="row justify-content-center">
            {items.map((slide) => (
                <div className="col-6 col-md-3 mb-4" key={slide.id}>
                    <ProductCardElement slidePro={slide} />
                </div>
            ))}
        </div>
    );

    return (
        <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '70vh', paddingTop: '30px' }}>
            <h1 className="mb-4">搜尋關鍵字</h1>
            <div ref={boxRef} style={{ position: 'relative', width: '100%', maxWidth: '520px' }}>
                <Form className="d-flex align-items-center" onSubmit={handleSubmit} role="search">
                    <FormControl
                        type="text"
                        placeholder={`試試搜尋：${searchSuggestions[currentSuggestion]}`}
                        aria-label="搜尋歌曲"
                        role="combobox"
                        aria-expanded={showSuggestions && suggestions.length > 0}
                        aria-controls="search-suggestions"
                        aria-autocomplete="list"
                        aria-activedescendant={activeIndex >= 0 ? `search-suggestion-${activeIndex}` : undefined}
                        onChange={handlechange}
                        onKeyDown={handleKeyDown}
                        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                        value={keyword}
                        className="flex-grow-1"
                    />
                    <Button variant="dark" type="submit" className="ms-3 flex-shrink-0" disabled={loading}>
                        {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-label="搜尋中" /> : '搜尋'}
                    </Button>
                </Form>
                {showSuggestions && suggestions.length > 0 && (
                    <ListGroup
                        id="search-suggestions"
                        role="listbox"
                        style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, marginTop: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', borderRadius: '0.5rem', overflow: 'hidden', maxHeight: '320px', overflowY: 'auto' }}
                    >
                        {suggestions.map((s, i) => (
                            <ListGroup.Item
                                key={s.id}
                                id={`search-suggestion-${i}`}
                                role="option"
                                aria-selected={i === activeIndex}
                                action
                                active={false}
                                onMouseEnter={() => setActiveIndex(i)}
                                onClick={() => pickSuggestion(s.song_name)}
                                style={{ cursor: 'pointer', textAlign: 'left', border: 0, backgroundColor: i === activeIndex ? '#f4f4f5' : '#fff' }}
                            >
                                <span style={{ fontWeight: 500 }}>{s.song_name}</span>
                                {s.author ? <span style={{ color: '#71717a', marginLeft: '8px', fontSize: '0.9rem' }}>{s.author}</span> : null}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}
            </div>

            {/* Results */}
            {hasResults && (
                <div className="w-100 mt-5">
                    <h4 className="mb-4">搜尋結果 "{searchParams.get('q')}"（{searchResult.length}）</h4>
                    {renderGrid(searchResult)}
                </div>
            )}

            {/* No results: don't dead-end — explain, then offer popular songs */}
            {searched && !loading && !hasResults && (
                <div className="w-100 mt-5 text-center">
                    <h4 className="mb-2">找不到「{searchParams.get('q')}」的結果</h4>
                    <p style={{ color: '#71717a' }}>試試更少的關鍵字，或看看以下熱門琴譜</p>
                    {popular.length > 0 && <div className="mt-4">{renderGrid(popular)}</div>}
                </div>
            )}

            {/* Initial state (no search yet): show popular songs as a starting point */}
            {!searched && !loading && popular.length > 0 && (
                <div className="w-100 mt-5 text-center">
                    <h5 className="mb-4" style={{ color: '#555' }}>熱門琴譜</h5>
                    {renderGrid(popular)}
                </div>
            )}
        </Container>
    );
};

export default SearchPage;
